// src/pages/Folder/Performance.tsx

import 'fix-date'
import { Collapse, Stack } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'

import { FolderId } from '@/types/domain'
import { SensorRecord, SensorPool, SensorId } from '@/types/domain/sensor'

import {
  A_MONTH_AGO,
  germanDecimals as decimals,
  MMMMYYYY,
  YYYY,
  sensorUnit,
  SORT_TIMEPOINT,
  userLocales as locales,
} from '../../api'
import {
  ConsumptionChart,
  PackageUpgrade,
  Preloader,
  usePreloader,
} from '../../components/'
import { svgConstants as SVG } from '../../constants'
import {
  useConsumptionApi,
  useConsumptionSensors,
  useSensorPool,
  useSensorPoolIds,
} from '../../hooks'

const generateColumns = (type: string) => [
  '',
  sensorUnit(type),
  { p: { html: true }, role: 'tooltip', type: 'string' },
]

type ReducedConsumption = {
  timepoint: string | number
  value: number
  tick?: unknown
  status?: string
}

type ChartRow = [Date | string, number, string]
type ChartTable = Array<unknown | ChartRow>
type Period = 'day' | 'month' | 'year' | 'decade'
type ChartByPeriod = Record<Period, ChartTable>

const FILTER_OLDER_THAN_A_MONTH = (f: ReducedConsumption) =>
  new Date(f.timepoint).getTime() > A_MONTH_AGO.getTime()

const FILTER_OLDER_THAN_A_YEAR = (f: ReducedConsumption) => {
  const NOW: number = Date.now()
  const A_YEAR_AGO: number = new Date(NOW).setFullYear(
    new Date(NOW).getFullYear() - 1
  )

  return new Date(f.timepoint).getTime() > A_YEAR_AGO
}

const FILTER_OLDER_THAN_YESTERDAY = (f: ReducedConsumption) => {
  const _1S: number = 1000
  const _1M: number = 60 * _1S
  const _1H: number = 60 * _1M
  const _24H: number = 24 * _1H
  const NOW: number = Date.now()
  const YESTERDAY: number = NOW - _24H
  const TZO: number = new Date().getTimezoneOffset() * _1M

  const d: Date = new Date(f.timepoint)
  d.setTime(d.getTime() - TZO)

  return d.getTime() > YESTERDAY
}

const DATA_REDUCER = (
  acc: Record<string | number, ReducedConsumption>,
  cur: ReducedConsumption
) => {
  const { timepoint } = cur

  acc[timepoint] = acc[timepoint] || ({} as ReducedConsumption)

  for (const attr in cur) {
    if (attr === 'tick') acc[timepoint].tick ??= cur.tick

    if (attr === 'timepoint') acc[timepoint].timepoint ??= cur.timepoint

    if (attr === 'value') {
      acc[timepoint].value =
        acc[timepoint].value !== undefined
          ? acc[timepoint].value + cur.value
          : cur.value
    }

    if (attr === 'status') {
      acc[timepoint].status = acc[timepoint].status
        ? acc[timepoint].status + ' und ' + cur.status
        : cur.status
    }
  }

  return acc
}

type PerformanceProps = {
  id: FolderId
}

export const Performance = ({ id }: PerformanceProps) => {
  const {
    getDailyConsumptionToDate,
    getMonthlyConsumptionToDate,
    getYearlyConsumptionToDate,
    getDecadlyConsumptionToDate,
  } = useConsumptionApi()

  const sPool: SensorPool = useSensorPool()
  const sIds: SensorId[] = useSensorPoolIds()
  const cSensors: SensorRecord[] = useConsumptionSensors(id)

  const { isLoading, percentage } = usePreloader()

  const [cDaily, setConsumptionDaily] = useState<any[]>([])
  const [cMonthly, setConsumptionMonthly] = useState<any[]>([])
  const [cYearly, setConsumptionYearly] = useState<any[]>([])
  const [cDecadly, setConsumptionDecadly] = useState<any[]>([])

  const [daily, setDaily] = useState<{
    energy: ChartTable
    water: ChartTable
    gas: ChartTable
  }>({ energy: [], water: [], gas: [] })

  const [monthly, setMonthly] = useState<{
    energy: ChartTable
    water: ChartTable
    gas: ChartTable
  }>({ energy: [], water: [], gas: [] })

  const [yearly, setYearly] = useState<{
    energy: ChartTable
    water: ChartTable
    gas: ChartTable
  }>({ energy: [], water: [], gas: [] })

  const [decadly, setDecadly] = useState<{
    energy: ChartTable
    water: ChartTable
    gas: ChartTable
  }>({ energy: [], water: [], gas: [] })

  const [hasMainSensor, setHasMainSensor] = useState({
    energy: false,
    water: false,
    gas: false,
  })

  const [energy, setEnergy] = useState<ChartByPeriod>({
    day: [],
    month: [],
    year: [],
    decade: [],
  })

  const [water, setWater] = useState<ChartByPeriod>({
    day: [],
    month: [],
    year: [],
    decade: [],
  })

  const [gas, setGas] = useState<ChartByPeriod>({
    day: [],
    month: [],
    year: [],
    decade: [],
  })

  useEffect(() => {
    const getDaily = async (id: FolderId) => {
      const daily = await getDailyConsumptionToDate(id)

      setConsumptionDaily(daily)
    }

    getDaily(id)
  }, [getDailyConsumptionToDate, id])

  useEffect(() => {
    const getMonthly = async (id: FolderId) => {
      const monthly = await getMonthlyConsumptionToDate(id)

      setConsumptionMonthly(monthly)
    }

    getMonthly(id)
  }, [getMonthlyConsumptionToDate, id])

  useEffect(() => {
    const getYearly = async (id: FolderId) => {
      const yearly = await getYearlyConsumptionToDate(id)

      setConsumptionYearly(yearly)
    }

    getYearly(id)
  }, [getYearlyConsumptionToDate, id])

  useEffect(() => {
    const getDecadly = async (id: FolderId) => {
      const decadly = await getDecadlyConsumptionToDate(id)

      setConsumptionDecadly(decadly)
    }

    getDecadly(id)
  }, [getDecadlyConsumptionToDate, id])

  useEffect(() => {
    const cIds: SensorId[] = [
      ...new Set([
        ...cDaily.map(item => item.entity_id),
        ...cMonthly.map(item => item.entity_id),
        ...cYearly.map(item => item.entity_id),
        ...cDecadly.map(item => item.entity_id),
      ]),
    ]

    const probeMainSensor = (id: FolderId, medium: string) =>
      sPool.some(
        (sensor: any) =>
          sensor.attributes.type_in_folder === medium &&
          Number(sensor.attributes.folder_id) === id &&
          cIds.includes(Number(sensor.attributes.entity_id))
      )

    setHasMainSensor({
      energy: probeMainSensor(id, 'energy'),
      water: probeMainSensor(id, 'water'),
      gas: probeMainSensor(id, 'gas'),
    })
  }, [cDaily, cMonthly, cYearly, cDecadly, id, sPool])

  useEffect(() => {
    const IS_ENERGY = (s: SensorRecord) => s.type_in_folder === 'energy'
    const IS_WATER = (s: SensorRecord) => s.type_in_folder === 'water'
    const IS_GAS = (s: SensorRecord) => s.type_in_folder === 'gas'

    const IS_USER_SENSOR = (s: SensorRecord) => sIds.includes(s.entity_id)

    const EXCLUDE_ENERGY = (s: SensorRecord) =>
      s.sub_sensor_target !== 'energy' && s.type_in_folder !== 'energy'

    const EXCLUDE_WATER = (s: SensorRecord) =>
      s.sub_sensor_target !== 'water' && s.type_in_folder !== 'water'

    const EXCLUDE_GAS = (s: SensorRecord) =>
      s.sub_sensor_target !== 'gas' && s.type_in_folder !== 'gas'

    let dailyEnergy = cDaily
      .filter(IS_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    dailyEnergy = Object.keys(dailyEnergy)
      .reduce<any[]>((acc, cur) => acc.concat(dailyEnergy[cur]), [])
      .filter(FILTER_OLDER_THAN_YESTERDAY)

    const dailyEnergyTable: ChartTable = Object.values(
      (dailyEnergy as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('energy')}`

        return [d, value, tooltip]
      })

    let dailyWater = cDaily
      .filter(IS_WATER)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    dailyWater = Object.keys(dailyWater)
      .reduce<any[]>((acc, cur) => acc.concat(dailyWater[cur]), [])
      .filter(FILTER_OLDER_THAN_YESTERDAY)

    const dailyWaterTable: ChartTable = Object.values(
      (dailyWater as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('water')}`

        return [d, value, tooltip]
      })

    let dailyGas = cDaily
      .filter(IS_GAS)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    dailyGas = Object.keys(dailyGas)
      .reduce<any[]>((acc, cur) => acc.concat(dailyGas[cur]), [])
      .filter(FILTER_OLDER_THAN_YESTERDAY)

    const dailyGasTable: ChartTable = Object.values(
      (dailyGas as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('gas')}`

        return [d, value, tooltip]
      })

    setDaily(prev => ({
      ...prev,
      energy: [generateColumns('energy'), ...dailyEnergyTable],
      water: [generateColumns('water'), ...dailyWaterTable],
      gas: [generateColumns('gas'), ...dailyGasTable],
    }))
  }, [cDaily, sIds])

  useEffect(() => {
    const IS_ENERGY = (s: SensorRecord) => s.type_in_folder === 'energy'
    const IS_WATER = (s: SensorRecord) => s.type_in_folder === 'water'
    const IS_GAS = (s: SensorRecord) => s.type_in_folder === 'gas'

    const IS_USER_SENSOR = (s: SensorRecord) => sIds.includes(s.entity_id)

    const EXCLUDE_ENERGY = (s: SensorRecord) =>
      s.sub_sensor_target !== 'energy' && s.type_in_folder !== 'energy'

    const EXCLUDE_WATER = (s: SensorRecord) =>
      s.sub_sensor_target !== 'water' && s.type_in_folder !== 'water'

    const EXCLUDE_GAS = (s: SensorRecord) =>
      s.sub_sensor_target !== 'gas' && s.type_in_folder !== 'gas'

    let monthlyEnergy = cMonthly
      .filter(IS_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    monthlyEnergy = Object.keys(monthlyEnergy)
      .reduce<any[]>((acc, cur) => acc.concat(monthlyEnergy[cur]), [])
      .filter(FILTER_OLDER_THAN_A_MONTH)

    const monthlyEnergyTable: ChartTable = Object.values(
      (monthlyEnergy as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('energy')}`

        return [d, value, tooltip]
      })

    let monthlyWater = cMonthly
      .filter(IS_WATER)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    monthlyWater = Object.keys(monthlyWater)
      .reduce<any[]>((acc, cur) => acc.concat(monthlyWater[cur]), [])
      .filter(FILTER_OLDER_THAN_A_MONTH)

    const monthlyWaterTable: ChartTable = Object.values(
      (monthlyWater as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('water')}`

        return [d, value, tooltip]
      })

    let monthlyGas = cMonthly
      .filter(IS_GAS)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    monthlyGas = Object.keys(monthlyGas)
      .reduce<any[]>((acc, cur) => acc.concat(monthlyGas[cur]), [])
      .filter(FILTER_OLDER_THAN_A_MONTH)

    const monthlyGasTable: ChartTable = Object.values(
      (monthlyGas as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: Date = new Date(timepoint)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('gas')}`

        return [d, value, tooltip]
      })

    setMonthly(prev => ({
      ...prev,
      energy: [generateColumns('energy'), ...monthlyEnergyTable],
      water: [generateColumns('water'), ...monthlyWaterTable],
      gas: [generateColumns('gas'), ...monthlyGasTable],
    }))
  }, [cMonthly, sIds])

  useEffect(() => {
    const IS_ENERGY = (s: SensorRecord) => s.type_in_folder === 'energy'
    const IS_WATER = (s: SensorRecord) => s.type_in_folder === 'water'
    const IS_GAS = (s: SensorRecord) => s.type_in_folder === 'gas'

    const IS_USER_SENSOR = (s: SensorRecord) => sIds.includes(s.entity_id)

    const EXCLUDE_ENERGY = (s: SensorRecord) =>
      s.sub_sensor_target !== 'energy' && s.type_in_folder !== 'energy'

    const EXCLUDE_WATER = (s: SensorRecord) =>
      s.sub_sensor_target !== 'water' && s.type_in_folder !== 'water'

    const EXCLUDE_GAS = (s: SensorRecord) =>
      s.sub_sensor_target !== 'gas' && s.type_in_folder !== 'gas'

    let yearlyEnergy = cYearly
      .filter(IS_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    yearlyEnergy = Object.keys(yearlyEnergy)
      .reduce<any[]>((acc, cur) => acc.concat(yearlyEnergy[cur]), [])
      .filter(FILTER_OLDER_THAN_A_YEAR)

    const yearlyEnergyTable: ChartTable = Object.values(
      (yearlyEnergy as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: string = new Date(timepoint).toLocaleString(locales, MMMMYYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('energy')}`

        return [d, value, tooltip]
      })

    let yearlyWater = cYearly
      .filter(IS_WATER)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    yearlyWater = Object.keys(yearlyWater)
      .reduce<any[]>((acc, cur) => acc.concat(yearlyWater[cur]), [])
      .filter(FILTER_OLDER_THAN_A_YEAR)

    const yearlyWaterTable: ChartTable = Object.values(
      (yearlyWater as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: string = new Date(timepoint).toLocaleString(locales, MMMMYYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('water')}`

        return [d, value, tooltip]
      })

    let yearlyGas = cYearly
      .filter(IS_GAS)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    yearlyGas = Object.keys(yearlyGas)
      .reduce<any[]>((acc, cur) => acc.concat(yearlyGas[cur]), [])
      .filter(FILTER_OLDER_THAN_A_YEAR)

    const yearlyGasTable: ChartTable = Object.values(
      (yearlyGas as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: string = new Date(timepoint).toLocaleString(locales, MMMMYYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('gas')}`

        return [d, value, tooltip]
      })

    setYearly(prev => ({
      ...prev,
      energy: [generateColumns('energy'), ...yearlyEnergyTable],
      water: [generateColumns('water'), ...yearlyWaterTable],
      gas: [generateColumns('gas'), ...yearlyGasTable],
    }))
  }, [cYearly, sIds])

  useEffect(() => {
    const IS_ENERGY = (s: SensorRecord) => s.type_in_folder === 'energy'
    const IS_WATER = (s: SensorRecord) => s.type_in_folder === 'water'
    const IS_GAS = (s: SensorRecord) => s.type_in_folder === 'gas'

    const IS_USER_SENSOR = (s: SensorRecord) => sIds.includes(s.entity_id)

    const EXCLUDE_ENERGY = (s: SensorRecord) =>
      s.sub_sensor_target !== 'energy' && s.type_in_folder !== 'energy'

    const EXCLUDE_WATER = (s: SensorRecord) =>
      s.sub_sensor_target !== 'water' && s.type_in_folder !== 'water'

    const EXCLUDE_GAS = (s: SensorRecord) =>
      s.sub_sensor_target !== 'gas' && s.type_in_folder !== 'gas'

    let decadlyEnergy = cDecadly
      .filter(IS_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    decadlyEnergy = Object.keys(decadlyEnergy).reduce<any[]>(
      (acc, cur) => acc.concat(decadlyEnergy[cur]),
      []
    )

    const decadlyEnergyTable: ChartTable = Object.values(
      (decadlyEnergy as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const date: string = new Date(timepoint).toLocaleString(locales, YYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('energy')}`

        return [date, value, tooltip]
      })

    let decadlyWater = cDecadly
      .filter(IS_WATER)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_GAS)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    decadlyWater = Object.keys(decadlyWater).reduce<any[]>(
      (acc, cur) => acc.concat(decadlyWater[cur]),
      []
    )

    const decadlyWaterTable: ChartTable = Object.values(
      (decadlyWater as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item
        const d: string = new Date(timepoint).toLocaleString(locales, YYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('water')}`

        return [d, value, tooltip]
      })

    let decadlyGas = cDecadly
      .filter(IS_GAS)
      .filter(EXCLUDE_ENERGY)
      .filter(EXCLUDE_WATER)
      .filter(IS_USER_SENSOR)
      .map(item => item.consumptions.values)

    decadlyGas = Object.keys(decadlyGas).reduce<any[]>(
      (acc, cur) => acc.concat(decadlyGas[cur]),
      []
    )

    const decadlyGasTable: ChartTable = Object.values(
      (decadlyGas as ReducedConsumption[]).reduce<
        Record<string | number, ReducedConsumption>
      >(DATA_REDUCER, {})
    )
      .sort(SORT_TIMEPOINT)
      .map(item => {
        const { timepoint, value } = item

        const d: string = new Date(timepoint).toLocaleString(locales, YYYY)
        const timestamp: string = value.toLocaleString(locales, decimals)
        const tooltip: string = `${timestamp} ${sensorUnit('gas')}`

        return [d, value, tooltip]
      })

    setDecadly(prev => ({
      ...prev,
      energy: [generateColumns('energy'), ...decadlyEnergyTable],
      water: [generateColumns('water'), ...decadlyWaterTable],
      gas: [generateColumns('gas'), ...decadlyGasTable],
    }))
  }, [cDecadly, sIds])

  useEffect(() => {
    if (isLoading) return

    setEnergy({
      day: daily.energy,
      month: monthly.energy,
      year: yearly.energy,
      decade: decadly.energy,
    })

    setWater({
      day: daily.water,
      month: monthly.water,
      year: yearly.water,
      decade: decadly.water,
    })

    setGas({
      day: daily.gas,
      month: monthly.gas,
      year: yearly.gas,
      decade: decadly.gas,
    })
  }, [isLoading, daily, monthly, yearly, decadly])

  const HAS_ENERGY_DATA =
    (energy.day?.length ?? 0) > 2 ||
    (energy.month?.length ?? 0) > 2 ||
    (energy.year?.length ?? 0) > 2 ||
    (energy.decade?.length ?? 0) > 2

  const HAS_WATER_DATA =
    (water.day?.length ?? 0) > 2 ||
    (water.month?.length ?? 0) > 2 ||
    (water.year?.length ?? 0) > 2 ||
    (water.decade?.length ?? 0) > 2

  const HAS_GAS_DATA =
    (gas.day?.length ?? 0) > 2 ||
    (gas.month?.length ?? 0) > 2 ||
    (gas.year?.length ?? 0) > 2 ||
    (gas.decade?.length ?? 0) > 2

  const NO_CONSUMPTION_SENSORS = cSensors.length === 0

  return isLoading ? (
    <Preloader
      caption={`Verbrauch laden ... ${percentage}%`}
      isLoading={isLoading}
      percentage={percentage}
    />
  ) : (
    <Stack>
      <Collapse in={NO_CONSUMPTION_SENSORS}>
        <PackageUpgrade id={id} />
      </Collapse>

      <Collapse in={HAS_ENERGY_DATA}>
        <ConsumptionChart
          hasMainSensor={hasMainSensor.energy}
          id={id}
          options={{ icon: SVG.MEDIUM_ENERGY, name: 'Strom', type: 'energy' }}
        >
          {energy as unknown as ReactNode}
        </ConsumptionChart>
      </Collapse>

      <Collapse in={HAS_WATER_DATA}>
        <ConsumptionChart
          hasMainSensor={hasMainSensor.water}
          id={id}
          options={{ icon: SVG.MEDIUM_WATER, name: 'Wasser', type: 'water' }}
        >
          {water as unknown as ReactNode}
        </ConsumptionChart>
      </Collapse>

      <Collapse in={HAS_GAS_DATA}>
        <ConsumptionChart
          hasMainSensor={hasMainSensor.gas}
          id={id}
          options={{ icon: SVG.MEDIUM_HEATING, name: 'Wärme', type: 'gas' }}
        >
          {gas as unknown as ReactNode}
        </ConsumptionChart>
      </Collapse>
    </Stack>
  )
}
